<template>
  <div class="ksk-mod">
    <!-- KSK021 — modifier groups as cards: name + Required/Optional pill + rule,
         then a 2-column grid of option tiles. -->
    <section
      v-for="(modifierGroup, index) in itemInfo.modifier_group"
      :key="index"
      class="ksk-mod-group"
      :class="{ 'ksk-mod-group--unmet': highlightUnmet && isUnmet(modifierGroup) }"
      :data-ksk-group="`${indexChoice}-${index}`"
    >
      <div class="ksk-mod-group__head">
        <h3 class="ksk-mod-group__name">{{ tName(modifierGroup.name) }}</h3>
        <span
          class="ksk-mod-group__pill"
          :class="{ 'ksk-mod-group__pill--required': modifierGroup.min_qty > 0 }"
        >
          {{ $t(modifierGroup.min_qty == 0 ? "kiosk.modifier.optional" : "kiosk.modifier.required") }}
        </span>
        <span
          v-if="ruleText(modifierGroup)"
          class="ksk-mod-group__rule"
          :class="{ 'select-min': isUnmet(modifierGroup) }"
        >
          {{ ruleText(modifierGroup) }}
        </span>
      </div>

      <div class="ksk-mod-grid">
        <div
          v-for="(item, index2) in modifierGroup.items"
          :key="index2"
          :id="`item_${indexChoice}_${index}_${index2}`"
          class="ksk-opt"
          v-bind="kskSlot('option_tile')"
          :class="{
            'ksk-opt--selected': item.selected,
            'ksk-opt--sold-out': item.is_not_available,
            'ksk-opt--at-max': !isSingle(modifierGroup) && isFadedAtMax(modifierGroup, index2),
          }"
          @click="
            isSingle(modifierGroup)
              ? handleItemClick(indexChoice, index, index2, item)
              : handleMultiItemClick(indexChoice, index, index2, item)
          "
        >
          <span
            class="ksk-opt__mark material-icons-round"
            :class="isSingle(modifierGroup) ? 'ksk-opt__mark--radio' : 'ksk-opt__mark--check'"
          >check</span>

          <img
            v-if="item.image && checkImage(item.image)"
            :src="item.image"
            alt=""
            class="ksk-opt__img"
          />

          <div class="ksk-opt__body">
            <span class="ksk-opt__name">{{ tName(item.item_name) }}</span>
            <span
              v-if="branch && branch.show_mog_description_on_kiosk && item.description"
              class="ksk-opt__desc"
            >{{ item.description }}</span>
            <span v-if="item.is_not_available" class="ksk-opt__sold-out">{{ $t("kiosk.common.soldOut") }}</span>

            <div
              v-if="hasNestedModifier(item) || showMiniStepper(modifierGroup, item)"
              class="ksk-opt__actions"
            >
              <button
                v-if="hasNestedModifier(item)"
                type="button"
                class="ksk-opt__customize"
                @click.stop="onShowMoreModifier(indexChoice, index, index2, item)"
              >
                <span class="material-icons-round">tune</span>
                {{ $t("kiosk.modifier.customize") }}
              </button>
              <div
                v-if="showMiniStepper(modifierGroup, item)"
                class="ksk-opt__stepper"
                @click.stop
              >
                <button
                  type="button"
                  :aria-label="$t('kiosk.common.decrease')"
                  @click="handleMultiQuantityRemove(indexChoice, index, item, index2)"
                >
                  <span class="material-icons-round">remove</span>
                </button>
                <span class="ksk-opt__qty">{{ item.quantity }}</span>
                <button
                  type="button"
                  :aria-label="$t('kiosk.common.increase')"
                  @click="handleMultiQuantityAdd(indexChoice, index, item, index2)"
                >
                  <span class="material-icons-round">add</span>
                </button>
              </div>
            </div>
          </div>

          <span
            v-if="!modifierGroup.is_remark && item.price > 0"
            class="ksk-opt__price"
          >+${{ formatPrice(item.price_default) }}</span>
        </div>
      </div>
    </section>
  </div>
</template>


<script>
import { localStorageName } from "@/config/utils";
import { kskSlot } from "@/config/kioskColorSlots";
import { translatedName } from "@/i18n/menuTranslate";

export default {
  name: "ItemModifier",
  components: {
  },
  props: {
    keyReload: {
      type: String,
      required: true,
    },
    itemInfo: {
      type: Object,
      required: true,
    },
    selectOnlyItem: {
      type: Function,
      default: () => {},
    },
    selectMultiItem: {
      type: Function,
      default: () => {},
    },
    removeModifierOptionGroup: {
      type: Function,
      default: () => {},
    },
    addModifierOptionGroup: {
      type: Function,
      default: () => {},
    },
    indexChoice: {
      type: Number,
      default: -1,
    },
    isLoadingModifiers: {
      type: Boolean,
      default: false,
    },
    onBeforeItemClick: {
      type: Function,
      default: () => {},
    },
    onAfterItemClick: {
      type: Function,
      default: () => {},
    },
    onShowMoreModifier: {
      type: Function,
      default: () => {},
    },
    // Set by ModalItemDetail after a failed Add to Cart: unmet required groups
    // get the red bar.
    highlightUnmet: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedItemForModifier: null,
      // Read branch settings from localStorage (populated by App.vue via /check-merchant-and-branch).
      // Used to gate kiosk display toggles like show_mog_description_on_kiosk.
      branch: JSON.parse(
        localStorage.getItem(localStorageName("BRANCH_INFO")) || "null"
      ),
    };
  },
  methods: {
    tName(source) {
      return translatedName(source);
    },
    kskSlot,
    formatPrice(price) {
      return new Intl.NumberFormat("en", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(price);
    },

    checkImage(image) {
      return image && !image.includes("noimage.png");
    },

    isSingle(group) {
      return group.min_qty == 1 && group.max_qty == 1;
    },

    isUnmet(group) {
      return group.min_qty > (group.total_selected || 0);
    },

    ruleText(group) {
      const min = group.min_qty;
      const max = group.max_qty;
      if (min == 0 && max > 0) return this.$t("kiosk.modifier.chooseUpTo", { max });
      if (min == 1 && max == 1) return this.$t("kiosk.modifier.chooseOne");
      if (min == max && max > 1) return this.$t("kiosk.modifier.chooseExact", { max, selected: group.total_selected || 0 });
      if (max > 0) return this.$t("kiosk.modifier.chooseRange", { min, max });
      return "";
    },

    // Multi-select group at its max: the unselected tiles fade.
    isFadedAtMax(group, index2) {
      return group.max_qty == group.total_selected
        ? !(group.items_selected || []).includes(index2)
        : false;
    },

    showMiniStepper(group, item) {
      return item.selected && item.item_max_qty > 1 && group.max_qty > 1;
    },

    // A selected modifier item can carry its own nested modifier groups
    // (item_in_mog). The kiosk layout can't render them inline, so we expose a
    // "Show more" button that re-opens the nested modifier modal on demand.
    hasNestedModifier(item) {
      return !!(
        item &&
        item.selected &&
        item.item_in_mog &&
        item.item_in_mog.id &&
        Array.isArray(item.item_in_mog.modifier_group) &&
        item.item_in_mog.modifier_group.length > 0
      );
    },

    async handleItemClick(indexChoice, index, index2, item) {
      const wasSelected = !!item.selected;
      this.onBeforeItemClick("only", indexChoice, index, index2);
      await this.selectOnlyItem(indexChoice, index, index2, item);
      await this.onAfterItemClick("only", indexChoice, index, index2, wasSelected);
    },

    async handleMultiItemClick(indexChoice, index, index2, item) {
      const wasSelected = !!item.selected;
      this.onBeforeItemClick("multi", indexChoice, index, index2);
      await this.selectMultiItem(indexChoice, index, index2, item);
      await this.onAfterItemClick("multi", indexChoice, index, index2, wasSelected);
    },

    handleMultiQuantityRemove(indexChoice, index, item, index2) {
      try {
        // check if quantity is 1, then unselect item
        if (item.quantity === 1) {
          this.handleMultiItemClick(indexChoice, index, index2, item);
        } else {
          this.removeModifierOptionGroup(indexChoice, index, index2);
        }
      } catch (error) {
        console.error('Error handling multi quantity remove:', error);
      }
    },

    handleMultiQuantityAdd(indexChoice, index, item, index2) {
      try {
        // Call the original function to handle the quantity change
        this.addModifierOptionGroup(indexChoice, index, index2);
      } catch (error) {
        console.error('Error handling multi quantity add:', error);
      }
    },
  },
  watch: {
    keyReload() {
      if (!this.itemInfo || !this.itemInfo.modifier_group) return;

      this.itemInfo.modifier_group.forEach((modifierGroup, index) => {
        if (modifierGroup.min_qty === 1 && modifierGroup.max_qty === 1) {
          modifierGroup.items.forEach((item, index2) => {            
            if (item.selected === true) {
              item.first_load = true;
              this.selectOnlyItem(this.indexChoice, index, index2, item);
            }
          });
        }
      });
    }
  }
};
</script>


<style scoped>
/* KSK021 — modifier group cards + option tiles (kiosk theme 2). */
.ksk-mod {
  display: flex;
  flex-direction: column;
  gap: var(--ksk-space-2);
}

.ksk-mod-group {
  position: relative;
  padding: var(--ksk-space-3);
  border: 1px solid var(--ksk-border);
  border-radius: var(--ksk-radius-card);
  background: var(--ksk-surface);
  box-shadow: var(--ksk-shadow-card);
  overflow: hidden;
  /* Clears the sticky title bar when scrolled to after a failed Add. */
  scroll-margin-top: 120px;
}
.ksk-mod-group--unmet::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 8px;
  background: var(--ksk-error);
}

.ksk-mod-group__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-bottom: var(--ksk-space-2);
}
.ksk-mod-group__name {
  margin: 0;
  font-family: var(--ksk-font);
  font-size: 26px;
  line-height: 34px;
  font-weight: 700;
  color: var(--ksk-text);
  overflow-wrap: break-word;
}
.ksk-mod-group__pill {
  flex: none;
  padding: 4px 14px;
  border: 1px solid var(--ksk-border);
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-primary-container);
  color: var(--ksk-primary);
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
}
.ksk-mod-group__pill--required {
  border-color: var(--ksk-error);
  background: var(--ksk-error);
  color: var(--ksk-on-status);
}
.ksk-mod-group__rule {
  margin-left: auto;
  font-size: 18px;
  line-height: 24px;
  font-weight: 500;
  color: var(--ksk-text-secondary);
}
.ksk-mod-group__rule.select-min {
  color: var(--ksk-error);
  font-weight: 600;
}

/* Option tiles */
.ksk-mod-grid {
  display: grid;
  /* 2 columns in the full sheet, 1 in narrower hosts (sub-category modal). */
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 12px;
}
.ksk-opt {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 88px;
  padding: 12px var(--ksk-space-2);
  border: 2px solid transparent;
  border-radius: 18px;
  background: var(--ksk-background);
  font-family: var(--ksk-font);
  color: var(--ksk-text);
  cursor: pointer;
  user-select: none;
  transition: transform 80ms ease-out, opacity 150ms ease-out;
  -webkit-tap-highlight-color: transparent;
}
.ksk-opt:active {
  transform: scale(0.98);
}
/* KSK063: option_tile colour slot (outline = selected ring, font = selected
   name, accent = tint + tick); theme values otherwise. */
.ksk-opt--selected {
  border-color: var(--ksk-slot-outline, var(--ksk-primary));
  background: color-mix(in srgb, var(--ksk-slot-accent, var(--ksk-primary)) 12%, #ffffff);
}
.ksk-opt--at-max,
.ksk-opt--sold-out {
  opacity: 0.4;
}
.ksk-opt--sold-out {
  cursor: not-allowed;
}

.ksk-opt__mark {
  flex: none;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--ksk-surface);
  border: 2px solid var(--ksk-border);
  color: transparent;
  font-size: 22px;
}
.ksk-opt__mark--radio {
  border-radius: 50%;
}
.ksk-opt__mark--check {
  border-radius: 10px;
}
.ksk-opt--selected .ksk-opt__mark {
  border-color: var(--ksk-slot-accent, var(--ksk-primary));
  background: var(--ksk-slot-accent, var(--ksk-primary));
  color: var(--ksk-on-primary);
}

.ksk-opt__img {
  flex: none;
  width: 120px;
  height: 120px;
  border-radius: 16px;
  object-fit: cover;
}

.ksk-opt__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.ksk-opt__name {
  font-size: 21px;
  line-height: 27px;
  font-weight: 600;
  overflow-wrap: break-word;
}
.ksk-opt--selected .ksk-opt__name {
  font-weight: 700;
  color: var(--ksk-slot-font, var(--ksk-primary));
}
.ksk-opt__desc {
  font-size: 16px;
  line-height: 22px;
  color: var(--ksk-text-secondary);
}
.ksk-opt__sold-out {
  align-self: flex-start;
  padding: 2px 12px;
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-error);
  color: var(--ksk-on-status);
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
}
.ksk-opt__price {
  flex: none;
  align-self: center;
  padding: 2px 12px;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  color: var(--ksk-on-primary);
  font-variant-numeric: tabular-nums;
  /* KSK081: theme pill; text black/white by theme lightness (--ksk-on-primary). */
  background: var(--ksk-primary);
  border-radius: var(--ksk-radius-pill);
}

.ksk-opt__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--ksk-space-1) 12px;
  margin-top: 6px;
}
.ksk-opt__customize {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 44px;
  padding: 0 16px;
  border: 1px solid var(--ksk-primary);
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-surface);
  color: var(--ksk-primary);
  font-family: var(--ksk-font);
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
}
.ksk-opt__customize .material-icons-round {
  font-size: 22px;
}
.ksk-opt__stepper {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 52px;
  padding: 0 4px;
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-surface);
  border: 1px solid var(--ksk-border);
}
.ksk-opt__stepper button {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: var(--ksk-surface-muted);
  color: var(--ksk-text);
  cursor: pointer;
}
.ksk-opt__stepper button:last-child {
  background: var(--ksk-primary);
  color: var(--ksk-on-primary);
}
.ksk-opt__stepper .material-icons-round {
  font-size: 26px;
}
.ksk-opt__qty {
  min-width: 36px;
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
</style>
